import "../components/auth/auth.card.css";

import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Center from "../components/layout/Center/Center";
import { registerUser } from "../clients/auth.client";

export const RegistrationPage = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorField, setErrorField] = useState("");
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const repeatedPasswordRef = useRef(null);

  const showError = (text, field, fieldRef) => {
    setMessage(text);
    setErrorField(field);
    window.requestAnimationFrame(() => fieldRef.current?.focus());
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    setMessage("");
    setIsSuccess(false);
    setErrorField("");

    const form = new FormData(formElement);
    const email = String(form.get("email") ?? "").trim();
    const password = String(form.get("password") ?? "");
    const repeatedPassword = String(form.get("repeatedPassword") ?? "");

    if (password.length < 8) {
      showError(t("auth.passwordLengthError"), "password", passwordRef);
      return;
    }

    if (password !== repeatedPassword) {
      showError(t("auth.passwordMismatchError"), "repeatedPassword", repeatedPasswordRef);
      return;
    }

    setIsSubmitting(true);
    const result = await registerUser({ email, password });
    setIsSubmitting(false);

    if (result.isError) {
      showError(
        result.status === 400
          ? t("auth.registrationInputError")
          : t("auth.registrationFailed"),
        "email",
        emailRef
      );
      return;
    }

    formElement.reset();
    setIsSuccess(true);
    setErrorField("");
    setMessage(t("auth.registrationSuccess"));
  };

  return (
    <Center useFreeHeightSpace>
      <form className="auth-card" onSubmit={handleSubmit}>
        <h1>{t("auth.createAccount")}</h1>

        <div className="auth-field">
          <label htmlFor="registration-email">{t("auth.email")}</label>
          <input
            aria-describedby={errorField === "email" ? "registration-message" : undefined}
            aria-invalid={errorField === "email"}
            autoComplete="email"
            id="registration-email"
            name="email"
            placeholder="user@example.com"
            ref={emailRef}
            required
            type="email"
          />
        </div>

        <div className="auth-field">
          <label htmlFor="registration-password">{t("auth.password")}</label>
          <input
            aria-describedby={errorField === "password" ? "registration-password-hint registration-message" : "registration-password-hint"}
            aria-invalid={errorField === "password"}
            autoComplete="new-password"
            id="registration-password"
            name="password"
            ref={passwordRef}
            required
            type="password"
          />
          <small id="registration-password-hint">
            {t("auth.passwordHint")}
          </small>
        </div>

        <div className="auth-field">
          <label htmlFor="registration-repeated-password">{t("auth.repeatPassword")}</label>
          <input
            aria-describedby={errorField === "repeatedPassword" ? "registration-message" : undefined}
            aria-invalid={errorField === "repeatedPassword"}
            autoComplete="new-password"
            id="registration-repeated-password"
            name="repeatedPassword"
            ref={repeatedPasswordRef}
            required
            type="password"
          />
        </div>

        {message && (
          <p
            className={isSuccess ? "auth-message auth-message-success" : "auth-message auth-message-error"}
            id="registration-message"
            role={isSuccess ? "status" : "alert"}
          >
            {message}
          </p>
        )}

        <button disabled={isSubmitting} type="submit">
          {isSubmitting ? t("auth.registering") : t("auth.register")}
        </button>

        <Link to="/auth/login">{t("auth.backToSignIn")}</Link>
      </form>
    </Center>
  );
};
